import type {Editor} from '@tiptap/react'
import clsx from 'clsx'
import {RichTextEditor} from 'common/components/richText/RichTextEditor'
import {FC, useId, useMemo} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useIntl} from 'react-intl'

type Props = {
  label?: string
  labelClassName?: string
  required?: boolean
  title?: string
  acceptImage?: boolean
  value?: string
  placeholder?: string
  onChange?: (value: string) => void
  onKeyDown?: (event: React.KeyboardEvent) => void
  readOnly?: boolean
  className?: string
  /** Called once with the editor instance, for callers that must drive it. */
  onEditorReady?: (editor: Editor) => void
}

export const TextEditor: FC<Props> = ({
  label,
  labelClassName,
  required = false,
  title,
  className,
  acceptImage = true,
  value = '',
  placeholder,
  onChange,
  onKeyDown,
  readOnly = false,
  onEditorReady,
}) => {
  const intl = useIntl()
  const id = useId()

  const labelHasTranslation = label ? !!intl.messages[label] : false
  const titleHasTranslation = title ? !!intl.messages[title] : false

  const renderTooltip = useMemo(() => {
    return (
      <Tooltip id={`tooltip-${id}`}>
        {titleHasTranslation ? intl.formatMessage({id: title}) : title}
      </Tooltip>
    )
  }, [title, intl, titleHasTranslation, id])

  return (
    <>
      {label && (
        <label
          className={clsx('form-label', labelClassName, {
            required,
          })}
          htmlFor={id}
        >
          {labelHasTranslation ? intl.formatMessage({id: label}) : label}
          {title && (
            <OverlayTrigger placement='right' overlay={renderTooltip}>
              <div className='badge pe-auto' style={{cursor: 'pointer'}}>
                <i className='bi bi-info-circle-fill'></i>
              </div>
            </OverlayTrigger>
          )}
        </label>
      )}
      <RichTextEditor
        id={id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        acceptImage={acceptImage}
        className={clsx('border rounded', className)}
        onKeyDown={onKeyDown}
        onEditorReady={onEditorReady}
      />
    </>
  )
}
